import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { AadhaarInput } from '@/components/ui/AadhaarInput';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { DatePicker } from '@/components/ui/DatePicker';
import { FileUpload } from '@/components/ui/FileUpload';
import { FormFieldWide, FormSection } from '@/components/ui/FormSection';
import { ImageUpload } from '@/components/ui/ImageUpload';
import { Input } from '@/components/ui/Input';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Select } from '@/components/ui/Select';
import { districtOptionsFor } from '@/constants/districts';
import { STATE_OPTIONS } from '@/constants/institute';
import type { InstituteClass } from '@/types/instituteClass';
import type { TransportRouteOption } from '@/types/student';
import {
  ADMISSION_TYPE_OPTIONS,
  BLOOD_GROUP_OPTIONS,
  DOCUMENT_KINDS,
  GENDER_OPTIONS,
  PRIMARY_CONTACT_OPTIONS,
  SOCIAL_CATEGORY_OPTIONS,
  type DocumentKind,
} from '../constants';
import type { AdmissionFormValues } from '../schemas/admission.schema';
import {
  ageMismatch,
  ageOn,
  classForAge,
  classOptions,
  describeAge,
  describeAgeRange,
  formatAge,
  sectionOptions,
  toWholeRupees,
} from '../utils';

/**
 * The admission form, section by section, in the order an office works
 * through one: who the child is, where they are being placed, who to ring,
 * where they live, how they travel, and what the papers say.
 *
 * They share one `react-hook-form` instance rather than holding state of their
 * own, so nothing is lost while scrolling and the whole admission is submitted
 * in a single request. The fee section lives in its own file — it carries a
 * running ledger, which is more than a group of fields.
 */
export interface SectionProps {
  form: UseFormReturn<AdmissionFormValues>;
}

// --- A. the child -----------------------------------------------------------

export interface PersonalSectionProps extends SectionProps {
  photo: File | null;
  onPhotoChange: (file: File | null) => void;
  /**
   * The photograph already on the record, shown in the frame until a new one is
   * picked. Empty at admission, where there is no record yet.
   */
  currentPhoto?: ReactNode;
  /**
   * Changes when the form is cleared. `ImageUpload` holds its own preview, so
   * without remounting it a cleared form would still show the last child's
   * photograph — on a screen used for one family after another, that is the
   * kind of mix-up an institute never lives down.
   */
  resetToken: number;
}

export function PersonalSection({
  form,
  photo,
  onPhotoChange,
  currentPhoto,
  resetToken,
}: PersonalSectionProps) {
  const {
    register,
    watch,
    formState: { errors },
  } = form;

  const age = describeAge(watch('dateOfBirth'));

  return (
    <FormSection
      title="A. The child"
      description="As it should read on the register, the report card and every certificate this institute will ever issue."
      aside={age ? <Badge tone="accent">{age} old</Badge> : undefined}
    >
      <Input
        label="First name"
        required
        placeholder="Aarav"
        autoComplete="off"
        error={errors.firstName?.message}
        {...register('firstName')}
      />
      <Input
        label="Middle name (optional)"
        autoComplete="off"
        error={errors.middleName?.message}
        {...register('middleName')}
      />
      <Input
        label="Last name"
        required
        placeholder="Sharma"
        autoComplete="off"
        error={errors.lastName?.message}
        {...register('lastName')}
      />
      <Input
        label="Student email (optional)"
        type="email"
        placeholder="aarav.sharma@example.com"
        autoComplete="off"
        hint="We email a link to set their own password, so they can sign in to the student panel. Leave blank if they have no address of their own."
        error={errors.email?.message}
        {...register('email')}
      />
      <Select
        label="Gender"
        required
        placeholder="Choose gender"
        options={GENDER_OPTIONS}
        error={errors.gender?.message}
        {...register('gender')}
      />

      <DatePicker
        label="Date of birth"
        required
        error={errors.dateOfBirth?.message}
        {...register('dateOfBirth')}
      />
      <Input
        label="Age today"
        readOnly
        tabIndex={-1}
        value={age ?? ''}
        placeholder="Worked out from the date of birth"
        className="bg-surface-2 font-mono"
        hint="Not stored — it is worked out from the date of birth every time."
      />

      <Select
        label="Blood group"
        options={BLOOD_GROUP_OPTIONS}
        hint="Leave as Not known until the institute has seen it in writing."
        error={errors.bloodGroup?.message}
        {...register('bloodGroup')}
      />
      <Select
        label="Category"
        required
        placeholder="Choose category"
        options={SOCIAL_CATEGORY_OPTIONS}
        hint="Used for RTE and scholarship reporting."
        error={errors.category?.message}
        {...register('category')}
      />
      <Input
        label="Religion (optional)"
        autoComplete="off"
        error={errors.religion?.message}
        {...register('religion')}
      />
      <Input
        label="Nationality"
        required
        autoComplete="off"
        error={errors.nationality?.message}
        {...register('nationality')}
      />
      <AadhaarInput
        label="Aadhaar number (optional)"
        hint="Needed for most government scholarships. It is stored encrypted."
        error={errors.aadhaarNumber?.message}
        {...register('aadhaarNumber')}
      />

      <div className="col-span-1 max-w-md">
        <ImageUpload
          key={resetToken}
          label="Student photo (optional)"
          accept="image/png,image/jpeg,image/webp"
          maxSizeMb={2}
          hint="Recent head photo. PNG, JPG or WEBP up to 2 MB."
          current={currentPhoto}
          onSelect={onPhotoChange}
          onRemove={photo ? () => onPhotoChange(null) : undefined}
        />
      </div>
    </FormSection>
  );
}

// --- B. placement -----------------------------------------------------------

export interface PlacementSectionProps extends SectionProps {
  classes: InstituteClass[];
  /** The record's roll number, shown read-only. Absent at admission and until one is assigned. */
  rollNumber?: string | null;
  /**
   * Leaves the fees alone when the class changes. On an existing record they
   * are what the child was charged at admission, shown read-only and never
   * sent with the save — refilling them from the new class only tripped the
   * concession and paid checks on figures the office cannot see or edit.
   */
  keepFees?: boolean;
  /**
   * A stored admission type the form can no longer offer (a retired quota type),
   * shown read-only in place of the picker until the office chooses to replace
   * it. The page leaves the type out of the save while `isReplacing` is false.
   */
  retiredAdmissionType?: { label: string; isReplacing: boolean; onReplace: () => void };
}

export function PlacementSection({
  form,
  classes,
  rollNumber,
  keepFees = false,
  retiredAdmissionType,
}: PlacementSectionProps) {
  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = form;

  const selectedClassUuid = watch('classUuid');
  const selectedClass = classes.find((instituteClass) => instituteClass.uuid === selectedClassUuid);
  const academicYear = classes[0]?.academicYear.name;

  // Age on the day the child actually joins, not today — an admission taken in
  // March for a June intake would otherwise be judged three months early.
  const age = ageOn(watch('dateOfBirth'), watch('admissionDate'));
  const suggestedClass = age ? classForAge(classes, age.years) : undefined;
  const mismatch = ageMismatch(selectedClass, age);
  // A transfer must name its previous institute and TC; the labels say so.
  const optionalUnlessTransfer = watch('admissionType') === 'TRANSFER' ? '' : ' (optional)';

  /**
   * What the class field says about itself. A field that fills in silently is
   * one an office will not trust enough to correct when it is wrong — and a
   * institute that has set no age ranges is promised nothing.
   */
  function classHint(): string | undefined {
    if (!classes.some((instituteClass) => describeAgeRange(instituteClass) !== null)) return undefined;
    if (!age) return 'Enter a date of birth above and this fills itself in.';
    if (suggestedClass?.uuid === selectedClassUuid) {
      return `Filled in from the date of birth — ${formatAge(age)} on the date of admission.`;
    }
    if (!suggestedClass) {
      return `No class here takes a child of ${formatAge(age)}. Choose one, or set the age range in class setup.`;
    }
    return `${suggestedClass.name} suits this age. Another class is fine if that is right.`;
  }

  const classField = register('classUuid');

  /**
   * Choosing a class decides two other things, so the form does them rather
   * than leaving the office to remember: last class's section is cleared, and
   * the fees fall back to what this class charges. Both stay editable — a
   * concession is entered against the child, not against the class.
   */
  const handleClassChange = useCallback(
    (uuid: string) => {
      const chosen = classes.find((instituteClass) => instituteClass.uuid === uuid);
      setValue('sectionUuid', '', { shouldValidate: false });
      if (!chosen || keepFees) return;

      setValue('tuitionFee', toWholeRupees(chosen.tuitionFee), { shouldValidate: true });
      setValue('booksFee', toWholeRupees(chosen.booksFee), { shouldValidate: true });
      setValue('otherFee', toWholeRupees(chosen.otherFees), { shouldValidate: true });
    },
    [classes, setValue, keepFees],
  );

  /**
   * The last class this filled in by itself. Anything else in the field was put
   * there by the office, and is never overwritten — a repeater placed in Class 8
   * must not jump back to Class 9 because the birth date was corrected after.
   */
  const filledIn = useRef<string | null>(null);

  useEffect(() => {
    if (!suggestedClass) return;

    const current = getValues('classUuid');
    if (current !== '' && current !== filledIn.current) return;
    if (current === suggestedClass.uuid) return;

    filledIn.current = suggestedClass.uuid;
    setValue('classUuid', suggestedClass.uuid, { shouldValidate: true });
    handleClassChange(suggestedClass.uuid);
  }, [suggestedClass, getValues, setValue, handleClassChange]);

  return (
    <FormSection
      title="B. Placement"
      description="Which class this child joins, and on what terms."
      aside={academicYear ? <Badge tone="neutral">{academicYear}</Badge> : undefined}
    >
      <Select
        label="Class"
        required
        options={classOptions(classes)}
        placeholder="Choose a class"
        hint={classHint()}
        error={errors.classUuid?.message}
        {...classField}
        onChange={(event) => {
          // The office has taken over from here; stop filling this in for them.
          filledIn.current = null;
          void classField.onChange(event);
          handleClassChange(event.target.value);
        }}
      />
      <Select
        label="Section"
        required
        options={sectionOptions(selectedClass)}
        placeholder={selectedClass ? 'Choose a section' : 'Choose a class first'}
        disabled={!selectedClass}
        error={errors.sectionUuid?.message}
        {...register('sectionUuid')}
      />

      {mismatch && (
        <FormFieldWide>
          <Alert tone="warning" title="This class does not match the age">
            {mismatch}
          </Alert>
        </FormFieldWide>
      )}

      {/* Not typed in: a section is numbered all at once from the students
          page, so the field only appears once there is a number to show. */}
      {rollNumber && (
        <Input
          label="Roll number"
          value={rollNumber}
          readOnly
          className="font-mono"
          hint="Given from the students page with Assign roll numbers."
        />
      )}
      {retiredAdmissionType && !retiredAdmissionType.isReplacing ? (
        <Input
          label="Admission type"
          value={retiredAdmissionType.label}
          readOnly
          hint={
            <>
              No longer offered for new admissions; saving keeps it as recorded.{' '}
              <button
                type="button"
                onClick={retiredAdmissionType.onReplace}
                className="font-semibold text-accent underline underline-offset-2 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Change the admission type
              </button>
            </>
          }
        />
      ) : (
        <Select
          label="Admission type"
          required
          options={ADMISSION_TYPE_OPTIONS}
          error={errors.admissionType?.message}
          {...register('admissionType')}
        />
      )}

      <DatePicker
        label="Date of admission"
        required
        error={errors.admissionDate?.message}
        {...register('admissionDate')}
      />
      <Input
        label="Previous class (optional)"
        placeholder="Class 7"
        autoComplete="off"
        error={errors.previousClass?.message}
        {...register('previousClass')}
      />

      <Input
        label={`Previous institute${optionalUnlessTransfer}`}
        required={!optionalUnlessTransfer}
        placeholder="Name of the institute last attended"
        autoComplete="off"
        error={errors.previousSchool?.message}
        {...register('previousSchool')}
      />
      <Input
        label={`Transfer certificate number${optionalUnlessTransfer}`}
        required={!optionalUnlessTransfer}
        placeholder="TC-8891"
        className="font-mono"
        autoComplete="off"
        error={errors.transferCertificateNumber?.message}
        {...register('transferCertificateNumber')}
      />

      <FormFieldWide>
        <Alert tone="info" title="The admission number is issued on saving">
          It follows your institute's own series, set under numbering in the institute profile — so no two
          children can ever be given the same one.
        </Alert>
      </FormFieldWide>
    </FormSection>
  );
}

// --- C. family --------------------------------------------------------------

export function FamilySection({ form }: SectionProps) {
  const {
    register,
    watch,
    formState: { errors },
  } = form;

  // Whoever the institute rings first must have a number on file, and a named
  // guardian needs one too — the same rules the schema enforces, shown up front.
  const primaryContact = watch('primaryContact');
  const needsFather = primaryContact === 'FATHER';
  const needsMother = primaryContact === 'MOTHER';
  const needsGuardianName = primaryContact === 'GUARDIAN';
  const needsGuardianMobile = needsGuardianName || Boolean(watch('guardianName')?.trim());
  const optionalUnless = (needed: boolean) => (needed ? '' : ' (optional)');

  return (
    <FormSection
      title="C. Parents and guardian"
      description="Who this institute talks to about this child — for a closure, an absence, or an emergency."
    >
      <Input
        label={`Father's name${optionalUnless(needsFather)}`}
        required={needsFather}
        autoComplete="off"
        error={errors.fatherName?.message}
        {...register('fatherName')}
      />
      <Input
        label="Father's occupation (optional)"
        autoComplete="off"
        error={errors.fatherOccupation?.message}
        {...register('fatherOccupation')}
      />
      <PhoneInput
        label={`Father's mobile${optionalUnless(needsFather)}`}
        required={needsFather}
        className="font-mono"
        autoComplete="off"
        error={errors.fatherMobile?.message}
        {...register('fatherMobile')}
      />
      {/* No longer optional as a pair: the student panel's invitation is sent
          to one of the two, so a record with neither is a child who can never
          sign in. Either will do, which is why neither is marked required on
          its own. */}
      <Input
        label="Father's email"
        type="email"
        autoComplete="off"
        hint="Either parent's email will do — the student's login link is sent to it"
        error={errors.fatherEmail?.message}
        {...register('fatherEmail')}
      />

      <Input
        label={`Mother's name${optionalUnless(needsMother)}`}
        required={needsMother}
        autoComplete="off"
        error={errors.motherName?.message}
        {...register('motherName')}
      />
      <Input
        label="Mother's occupation (optional)"
        autoComplete="off"
        error={errors.motherOccupation?.message}
        {...register('motherOccupation')}
      />
      <PhoneInput
        label={`Mother's mobile${optionalUnless(needsMother)}`}
        required={needsMother}
        className="font-mono"
        autoComplete="off"
        error={errors.motherMobile?.message}
        {...register('motherMobile')}
      />
      <Input
        label="Mother's email"
        type="email"
        autoComplete="off"
        error={errors.motherEmail?.message}
        {...register('motherEmail')}
      />

      <Input
        label={`Guardian's name${optionalUnless(needsGuardianName)}`}
        required={needsGuardianName}
        hint="Only where someone other than a parent is responsible for the child."
        autoComplete="off"
        error={errors.guardianName?.message}
        {...register('guardianName')}
      />
      <Input
        label="Relationship to the child (optional)"
        placeholder="Uncle, grandmother"
        autoComplete="off"
        error={errors.guardianRelationship?.message}
        {...register('guardianRelationship')}
      />
      <PhoneInput
        label={`Guardian's mobile${optionalUnless(needsGuardianMobile)}`}
        required={needsGuardianMobile}
        className="font-mono"
        autoComplete="off"
        error={errors.guardianMobile?.message}
        {...register('guardianMobile')}
      />
      <Select
        label="Who the institute rings first"
        required
        options={PRIMARY_CONTACT_OPTIONS}
        hint="The number an emergency goes to before any other."
        error={errors.primaryContact?.message}
        {...register('primaryContact')}
      />
    </FormSection>
  );
}

// --- D. address -------------------------------------------------------------

export function AddressSection({ form }: SectionProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const state = watch('state');
  const districts = districtOptionsFor(state);
  const stateField = register('state');

  return (
    <FormSection
      title="D. Where the child lives"
      description="The address on the institute's own records. It is stored encrypted."
    >
      <FormFieldWide>
        <Input
          label="Address line 1"
          required
          placeholder="Flat or house number, building, street"
          autoComplete="off"
          error={errors.addressLine1?.message}
          {...register('addressLine1')}
        />
      </FormFieldWide>
      <FormFieldWide>
        <Input
          label="Address line 2 (optional)"
          placeholder="Area, landmark"
          autoComplete="off"
          error={errors.addressLine2?.message}
          {...register('addressLine2')}
        />
      </FormFieldWide>

      <Select
        label="State"
        required
        options={STATE_OPTIONS}
        placeholder="Choose a state"
        error={errors.state?.message}
        {...stateField}
        onChange={(event) => {
          void stateField.onChange(event);
          // The old district belongs to the old state, so it cannot stand.
          setValue('district', '', { shouldValidate: false });
        }}
      />
      <Select
        label="District (optional)"
        options={districts}
        placeholder={state ? 'Choose a district' : 'Choose a state first'}
        disabled={districts.length === 0}
        error={errors.district?.message}
        {...register('district')}
      />
      <Input
        label="City or town"
        required
        autoComplete="off"
        error={errors.city?.message}
        {...register('city')}
      />
      <Input
        label="PIN code (optional)"
        inputMode="numeric"
        placeholder="6 digits"
        className="font-mono"
        autoComplete="off"
        error={errors.pincode?.message}
        {...register('pincode')}
      />
    </FormSection>
  );
}

// --- E. transport -----------------------------------------------------------

export interface TransportSectionProps extends SectionProps {
  /** Routes the institute already uses, each with its buses. Empty while loading or for a first admission. */
  routes: TransportRouteOption[];
}

/** Route names compared as the office would: ignoring case and stray spaces. */
const routeKey = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();

export function TransportSection({ form, routes }: TransportSectionProps) {
  const {
    register,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = form;

  const usesTransport = watch('usesTransport');
  const routeName = watch('routeName');

  // The buses offered follow the route: a known route offers only its own,
  // a new or empty one offers every bus the institute runs.
  const chosenRoute = routes.find((option) => routeKey(option.route) === routeKey(routeName ?? ''));
  const busSuggestions = chosenRoute
    ? chosenRoute.buses
    : [...new Set(routes.flatMap((option) => option.buses))].sort((a, b) => a.localeCompare(b));

  const routeField = register('routeName');

  return (
    <FormSection
      title="E. Institute transport"
      description="Only if this child travels on an institute bus."
      columns={1}
    >
      <Checkbox label="This child needs the institute bus" {...register('usesTransport')} />

      {usesTransport && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="Route"
            required
            placeholder={routes.length > 0 ? 'Pick or type a route' : 'As your institute names it'}
            autoComplete="off"
            hint={routes.length > 0 ? 'Pick one your institute already uses, or type a new one.' : undefined}
            suggestions={routes.map((option) => option.route)}
            error={errors.routeName?.message}
            {...routeField}
            onChange={(event) => {
              void routeField.onChange(event);
              // A bus from the old route is cleared once the new route is one
              // we know and that bus is not on it; a new route keeps the bus.
              const next = routes.find((option) => routeKey(option.route) === routeKey(event.target.value));
              const bus = getValues('busNumber');
              if (next && bus && !next.buses.some((known) => routeKey(known) === routeKey(bus))) {
                setValue('busNumber', '', { shouldDirty: true });
              }
            }}
          />
          <Input
            label="Bus number (optional)"
            placeholder={busSuggestions.length > 0 ? 'Pick or type a bus' : 'Bus 04'}
            autoComplete="off"
            hint={chosenRoute && chosenRoute.buses.length > 0 ? `Buses on ${chosenRoute.route}.` : undefined}
            suggestions={busSuggestions}
            error={errors.busNumber?.message}
            {...register('busNumber')}
          />
          <Input
            label="Pickup point"
            required
            placeholder="Main gate, stop 4"
            autoComplete="off"
            error={errors.pickupPoint?.message}
            {...register('pickupPoint')}
          />
        </div>
      )}
    </FormSection>
  );
}

// --- G. documents -----------------------------------------------------------

export interface DocumentsSectionProps {
  documents: Partial<Record<DocumentKind, File>>;
  onDocumentChange: (kind: DocumentKind, file: File | null) => void;
  /** The ones this admission cannot be saved without — see `requiredDocuments`. */
  required: readonly DocumentKind[];
  /** A message per slot, once the office has tried to save without it. */
  errors?: Partial<Record<DocumentKind, string>>;
}

export function DocumentsSection({ documents, onDocumentChange, required, errors }: DocumentsSectionProps) {
  return (
    <FormSection
      title="G. Documents"
      description="Scans or photographs of the papers collected at admission. Which ones are needed depends on the admission type and category chosen above."
    >
      {DOCUMENT_KINDS.map((kind) => (
        <FileUpload
          key={kind.key}
          label={required.includes(kind.key) ? kind.label : `${kind.label} (optional)`}
          required={required.includes(kind.key)}
          hint={kind.hint}
          error={errors?.[kind.key]}
          file={documents[kind.key] ?? null}
          onSelect={(file) => onDocumentChange(kind.key, file)}
        />
      ))}
    </FormSection>
  );
}
